import { useCallback, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';

// Module-level store — shared across all hook instances so all consumers
// re-render together when the theme changes.
type Listener = () => void;
const listeners = new Set<Listener>();

const readStoredMode = (): ThemeMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'system';
};

let _themeMode: ThemeMode = readStoredMode();

const notifyListeners = () => listeners.forEach((l) => l());

const subscribeThemeMode = (listener: Listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getThemeModeSnapshot = () => _themeMode;

const subscribeSystemDark = (listener: Listener) => {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', listener);
  return () => mq.removeEventListener('change', listener);
};

const getSystemDarkSnapshot = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

// Keep the DOM in sync when system preference changes while in system mode
window
  .matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', () => {
    if (_themeMode === 'system') {
      applyTheme('system');
    }
  });

export const applyTheme = (
  mode: ThemeMode,
  systemDark: boolean = window.matchMedia('(prefers-color-scheme: dark)')
    .matches,
) => {
  const isDark = mode === 'dark' || (mode === 'system' && systemDark);
  if (isDark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

// Exposed for test cleanup only
export const __resetForTesting = () => {
  _themeMode = 'system';
  notifyListeners();
};

const useDarkModeToggle = () => {
  const themeMode = useSyncExternalStore(
    subscribeThemeMode,
    getThemeModeSnapshot,
  );
  const systemDark = useSyncExternalStore(
    subscribeSystemDark,
    getSystemDarkSnapshot,
  );

  const setThemeMode = useCallback((mode: ThemeMode) => {
    _themeMode = mode;
    if (mode === 'system') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, mode);
    }
    applyTheme(mode);
    notifyListeners();
  }, []);

  const isDarkMode =
    themeMode === 'dark' || (themeMode === 'system' && systemDark);

  return { themeMode, setThemeMode, isDarkMode };
};

export default useDarkModeToggle;
